import styled, { css } from 'styled-components';

import { FistulaTypeField } from '../fields/FistulaType';
import {
   MedicalDeviceOrderFormMethods,
   MedicalDeviceOrderFormSetValue,
   MedicalDeviceOrderFormWatch,
   MedicalDeviceOrderItemsCountFormBeanPart,
   MedicalDeviceOrderPlacementFormBeanPart,
   MedicalDeviceOrderRefundLimitMultiplicityFormBeanPart,
   MedicalDeviceOrderTillLimitFormBeanPart,
} from '../medicalDeviceOrder.types';
import {
   isMedicalDeviceFieldAvailable,
   shouldDisplayFirstSupplyMonthField,
   shouldDisplayFistulaCountField,
   shouldDisplayFistulaTypeField,
   shouldDisplayItemsCountField,
   shouldDisplayItemsCountReadOnlyField,
   shouldDisplayItemsPerMonthCountField,
   shouldDisplayPlacementField,
   shouldDisplayRefundLimitMultiplicityField,
   shouldDisplaySupplyMonthsCountField,
} from '../medicalDeviceOrder.utils';

import { FirstSupplyMonthField } from './FirstSupplyMonthField';
import { FistulaCountField } from './FistulaCountField';
import { ItemsCountByPlacementField } from './ItemsCountByPlacementField';
import { ItemsCountField } from './ItemsCountField';
import { ItemsCountReadOnlyField } from './ItemsCountReadOnlyField';
import { ItemsPerMonthCountField } from './ItemsPerMonthCountField';
import { RefundLimitMultiplicityField } from './RefundLimitMultiplicityField';
import { SupplyMonthsCountField } from './SupplyMonthsCountField';

type MedicalDeviceFormDeviceSupplySectionProps = MedicalDeviceOrderFormMethods<
   unknown,
   'setValue' | 'register' | 'watch' | 'formState' | 'trigger'
>;

type StyledMedicalDeviceFormDeviceSupplySectionProps = {
   // eslint-disable-next-line @typescript-eslint/no-magic-numbers
   $complex: boolean;
};
const StyledMedicalDeviceFormDeviceSupplySectionGrid = styled.div<StyledMedicalDeviceFormDeviceSupplySectionProps>`
   display: grid;
   ${(props) =>
      !props.$complex &&
      css`
         grid-template-columns: repeat(3, 214px);
         grid-template-rows: 1fr;
         grid-column-gap: 24px;
      `}
   ${(props) =>
      props.$complex &&
      css`
         grid-template-columns: repeat(2, 214px);
         grid-template-rows: 1fr auto;
         grid-column-gap: 90px;
         grid-row-gap: 24px;
      `}
   &:empty {
      display: none;
   }
`;
StyledMedicalDeviceFormDeviceSupplySectionGrid.displayName = 'StyledMedicalDeviceFormDeviceSupplySectionGrid';

// eslint-disable-next-line max-lines-per-function, complexity
export const MedicalDeviceFormDeviceSupplySection = (props: MedicalDeviceFormDeviceSupplySectionProps): JSX.Element => {
   const { register, watch, setValue, formState, trigger } = props;
   const { fieldsConfiguration, limit } = watch('device') ?? {};

   const complexLayout = fieldsConfiguration
      ? fieldsConfiguration.refundLimitMultiplicity.available ||
        ((fieldsConfiguration.itemsCount.available || fieldsConfiguration.fistulaCount.available) &&
           fieldsConfiguration.itemsPerMonthCount.available &&
           fieldsConfiguration.firstSupplyDate.available &&
           fieldsConfiguration.supplyMonthsCount.available)
      : false;

   const placementFieldVisible = shouldDisplayPlacementField(
      register,
      isMedicalDeviceFieldAvailable(watch, 'placement')
   );
   const itemsCountFieldVisible = shouldDisplayItemsCountField(
      register,
      isMedicalDeviceFieldAvailable(watch, 'itemsCount'),
      limit ?? 0
   );

   return (
      <>
         {shouldDisplayFistulaTypeField(register, isMedicalDeviceFieldAvailable(watch, 'fistulaType')) ? (
            <FistulaTypeField register={register} watch={watch} />
         ) : null}
         <StyledMedicalDeviceFormDeviceSupplySectionGrid $complex={complexLayout}>
            {itemsCountFieldVisible && placementFieldVisible ? (
               <ItemsCountByPlacementField
                  register={register}
                  watch={
                     watch as MedicalDeviceOrderFormWatch<
                        MedicalDeviceOrderItemsCountFormBeanPart & MedicalDeviceOrderPlacementFormBeanPart
                     >
                  }
                  setValue={
                     setValue as MedicalDeviceOrderFormSetValue<
                        MedicalDeviceOrderItemsCountFormBeanPart & MedicalDeviceOrderPlacementFormBeanPart
                     >
                  }
                  trigger={trigger}
                  formState={formState}
               />
            ) : null}
            {itemsCountFieldVisible && !placementFieldVisible ? (
               <ItemsCountField
                  register={register}
                  watch={watch as MedicalDeviceOrderFormWatch<MedicalDeviceOrderItemsCountFormBeanPart>}
                  setValue={setValue as MedicalDeviceOrderFormSetValue<MedicalDeviceOrderItemsCountFormBeanPart>}
                  trigger={trigger}
                  formState={formState}
               />
            ) : null}
            {shouldDisplayItemsCountReadOnlyField(
               register,
               isMedicalDeviceFieldAvailable(watch, 'itemsCount'),
               limit ?? 0
            ) ? (
               <ItemsCountReadOnlyField
                  watch={watch as MedicalDeviceOrderFormWatch<MedicalDeviceOrderPlacementFormBeanPart>}
                  register={register}
                  setValue={setValue}
               />
            ) : null}
            {shouldDisplayFistulaCountField(register, isMedicalDeviceFieldAvailable(watch, 'fistulaCount')) ? (
               <FistulaCountField register={register} watch={watch} />
            ) : null}
            {shouldDisplayRefundLimitMultiplicityField(
               register,
               isMedicalDeviceFieldAvailable(watch, 'refundLimitMultiplicity')
            ) ? (
               <RefundLimitMultiplicityField
                  register={register}
                  watch={watch as MedicalDeviceOrderFormWatch<MedicalDeviceOrderRefundLimitMultiplicityFormBeanPart>}
               />
            ) : null}
            {shouldDisplayItemsPerMonthCountField(
               register,
               isMedicalDeviceFieldAvailable(watch, 'itemsPerMonthCount')
            ) ? (
               <ItemsPerMonthCountField
                  register={register}
                  watch={watch as MedicalDeviceOrderFormWatch<MedicalDeviceOrderTillLimitFormBeanPart>}
                  setValue={setValue}
               />
            ) : null}
            {shouldDisplayFirstSupplyMonthField(register, isMedicalDeviceFieldAvailable(watch, 'firstSupplyDate')) ? (
               <FirstSupplyMonthField register={register} watch={watch} setValue={setValue} />
            ) : null}
            {shouldDisplaySupplyMonthsCountField(
               register,
               isMedicalDeviceFieldAvailable(watch, 'supplyMonthsCount')
            ) ? (
               <SupplyMonthsCountField register={register} watch={watch} />
            ) : null}
         </StyledMedicalDeviceFormDeviceSupplySectionGrid>
      </>
   );
};
